import { useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons';
import { LoadingPage } from '../../loading-page';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { SuperAdminSurveyTemplateService } from '../../../../shared/services/survey-template/survey-template.service';
import { ISurveyTemplate } from '../../../../shared/services/survey-template/survey-template.model';
import { SuperAdminSurveyTemplateCreateDialog } from '../../../dialogs/super-admin/super-admin-survey-template-create-dialog';

const service = new SuperAdminSurveyTemplateService();

const headCells: IHeadCell[] = [
  { id: 'name', label: 'Name', numeric: false, sortable: false },
  { id: 'description', label: 'Description', numeric: false, sortable: false },
  { id: 'questions', label: 'Questions', numeric: true, sortable: false },
  { id: 'rules', label: 'Rules', numeric: true, sortable: false },
  { id: 'status', label: 'Status', numeric: false, sortable: false },
  { id: 'actions', label: '', numeric: false, sortable: false },
];

export const SuperAdminSurveyTemplatesPage = () => {
  const navigate = useNavigate();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [templates, setTemplates] = useState<ISurveyTemplate[]>();
  const [selected, setSelected] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setTemplates(undefined);
    service.getAllWithPagination(0, 100, 'name', 'asc')
      .then(res => setTemplates(res.rows))
      .catch(() => setTemplates([]));
  };

  useEffect(load, []);

  const handleDelete = (template: ISurveyTemplate) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete template',
      message: `Delete "${template.name}"? Surveys already created from it are their own copies and are not affected.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await service.delete(template.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Template deleted', backgroundColor: 'primary.dark' });
        load();
      } catch (err: any) {
        userNotificationsContext.openSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to delete template', backgroundColor: 'text.primary' });
      }
    });
  };

  const rows = (templates ?? []).map(t => ({
    id: t.id,
    name: t.name,
    description: t.description ?? '—',
    questions: t.questionCount ?? '—',
    rules: t.emissionRuleCount ?? '—',
    status: t.isPublished
      ? <Chip label='Published' color='success' size='small' />
      : <Chip label='Draft' size='small' />,
    actions: (
      <TableActionButtons
        id={t.id}
        includeView
        onView={() => navigate(`/super-admin/survey-templates/${t.id}`)}
        includeDelete
        onDelete={() => handleDelete(t)}
      />
    ),
  }));

  return (
    <PageOuterContainer>
      <PageHeading
        title='Survey Templates'
        includeActionButton
        actionButtonText='New Template'
        actionButtonIcon={IconName.Add}
        actionButtonVariant='contained'
        actionButtonHandler={() => setCreateOpen(true)}
        includeBackButton
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
      />
      <LoadingPage pageReady={!!templates}>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          onRowClick={(id: string) => navigate(`/super-admin/survey-templates/${id}`)}
        />
      </LoadingPage>
      <SuperAdminSurveyTemplateCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={(newId) => navigate(`/super-admin/survey-templates/${newId}`)}
      />
    </PageOuterContainer>
  );
};
